import { extend } from "node_modules/zod/v4/core/util.cjs";
import { z } from "zod";

export const ChannelSchema = z.enum(["Air", "Sea"]);

export const CountrySchema = z.enum(["China", "Nigeria", "Ghana"]);

export const CountryCodeSchema = z.enum(["NG", "GH"]);

export const OriginCountryCodeSchema = z.enum(["CH"]);

export const UnitSchema = z.enum(["kg", "cbm"]);

export const ShipProcessingFeeCurrencySchema = z.enum(["USD"]);

export const ShipProcessingFeeSchema = z.object({
  id: z.number(),
  processingfee_id: z.string(),
  country: CountryCodeSchema,
  start_range: z.number(),
  end_range: z.number(),
  channel: ChannelSchema,
  unit: UnitSchema,
  value: z.number(),
  currency: ShipProcessingFeeCurrencySchema,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const ShipProcessingFeeListSchema = z.array(ShipProcessingFeeSchema);

export const ShipProcessingFeeResponseSchema = z.object({
  message: z.string(),
  data: ShipProcessingFeeListSchema.nullable(),
});

export const TrackShipmentStagesSchema = z.object({
  stage_id: z.string(),
  title: z.string(),
  description: z.string(),
  duration_hours: z.number(),
  icon_file: z.string(),
  entered_at: z.string(),
  exited_at: z.string().optional(),
});

export const InternationalShipStatusEnum = z.enum([
  "pending",
  "in_transit",
  "due",
  "delayed",
  "arrived",
  "payment_pending",
  "pickup_code",
  "cancelled",
  "completed",
]);

export const InternationalShipStatusSchema = z
  .preprocess((val) => {
    // Map legacy 'ready_pickup' to the new 'completed'
    if (val === "ready_pickup") return "completed";
    return val;
  }, z.string())
  .pipe(InternationalShipStatusEnum);

export const ShipmentStatusEnum = z.enum([
  "pending",
  "in_transit",
  "arrived",
  "completed",
  "cancelled",
  "failed",
]);

export const ShipmentStatusSchema = z
  .preprocess((val) => {
    // Map legacy 'in_progress' to the new 'in_transit'
    if (val === "in_progress") return "in_transit";
    return val;
  }, z.string())
  .pipe(ShipmentStatusEnum);

export const TrackShipmentSchema = z.object({
  shipment_id: z.string(),
  overall_status: ShipmentStatusSchema,
  customer_name: z.string(),
  customer_phone: z.string(),
  origin_country: z.string(),
  destination_country: z.string(),
  receiver_name: z.string(),
  receiver_phone: z.string(),
  international_shipment: z.object({
    int_shipment_id: z.string(),
    status: InternationalShipStatusSchema,
    channel: ChannelSchema,
    next_transition: z.string().optional(),
    current_stage: z.object({
      stage_id: z.string(),
      title: z.string(),
      description: z.string(),
      duration_hours: z.number(),
      icon_file: z.string(),
    }),
    history: z.array(TrackShipmentStagesSchema),
  }),
});

export const TrackShipmentResponseSchema = z.object({
  message: z.string(),
  data: TrackShipmentSchema,
});

export const ShipmentTrackerURLSchema = z.object({
  message: z.string(),
  data: z.string(),
});

export const ShipmentCalculatorBaseSchema = z.object({
  currency: z.literal("USD"),
  processing_fee_1: z.number(),
  vat_1: z.number(),
  int_shipping_fee_1: z.number(),
  clearance_cost_1: z.number(),
});

export const ShipmentCalculatorBaseResponseSchema = z.object({
  message: z.string(),
  data: ShipmentCalculatorBaseSchema,
});

const ShipmentCalculatorAirDoubleWeightOnlyFieldsSchema = z.object({
  processing_fee_2: z.number(),
  vat_2: z.number(),
  int_shipping_fee_2: z.number(),
  clearance_cost_2: z.number(),
});

export const ShipmentCalculatorAirDoubleWeightSchema = ShipmentCalculatorBaseSchema.extend(
  ShipmentCalculatorAirDoubleWeightOnlyFieldsSchema.shape,
);

export const ShipmentCalculatorAirDoubleWeightResponseSchema = z.object({
  message: z.string(),
  data: ShipmentCalculatorAirDoubleWeightSchema,
});

export type ShipProcessingFee = z.infer<typeof ShipProcessingFeeSchema>;
export type ShipProcessingFeeResponse = z.infer<typeof ShipProcessingFeeResponseSchema>;
export type TrackShipment = z.infer<typeof TrackShipmentSchema>;
export type TrackShipmentResponse = z.infer<typeof TrackShipmentResponseSchema>;
export type ShipmentCalculatorBase = z.infer<typeof ShipmentCalculatorBaseSchema>;
export type ShipmentCalculatorBaseResponse = z.infer<typeof ShipmentCalculatorBaseResponseSchema>;
export type ShipmentCalculatorAirDoubleWeight = z.infer<
  typeof ShipmentCalculatorAirDoubleWeightSchema
>;
export type ShipmentCalculatorAirDoubleWeightResponse = z.infer<
  typeof ShipmentCalculatorAirDoubleWeightResponseSchema
>;

